
import React, { useState, useEffect } from 'react';
import { X, FileText, Upload, AlertCircle, Check } from 'lucide-react';
import { parseImportText } from '../utils/importParser';
import { Idea, Section, Attachment } from '../types';

interface ImportProjectModalProps {
    isOpen: boolean;
    onClose: () => void;
    onImport: (parsedData: { title: string, content: string, sections: Section[], attachments: Attachment[] }) => void;
    existingTitle?: string; // If provided, we are in "Merge" mode
}

export const ImportProjectModal: React.FC<ImportProjectModalProps> = ({ isOpen, onClose, onImport, existingTitle }) => {
    const [text, setText] = useState('');
    const [parsedPreview, setParsedPreview] = useState<{ title: string, sections: Section[], attachments: Attachment[] } | null>(null);

    useEffect(() => {
        if (isOpen) {
            setText('');
            setParsedPreview(null);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!text.trim()) {
            setParsedPreview(null);
            return;
        }
        const parsed = parseImportText(text, existingTitle || "New Imported Project");
        setParsedPreview({
            title: parsed.title,
            sections: parsed.sections,
            attachments: parsed.attachments
        });
    }, [text, existingTitle]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="w-[800px] max-h-[90vh] bg-cc-surface rounded-xl shadow-2xl border border-cc-border flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">

                {/* Header */}
                <div className="px-6 py-4 border-b border-cc-border flex items-center justify-between bg-cc-elevated">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-cc-accent/10 rounded-lg">
                            <Upload size={20} className="text-cc-accent" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-cc-text">{existingTitle ? 'Import to Project' : 'Import Project'}</h2>
                            <p className="text-xs text-cc-text-muted">Paste your formatted text below to create or update transparency.</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-cc-tile-hover rounded-lg text-cc-text-muted hover:text-red-500 transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-hidden flex flex-col md:flex-row">

                    {/* Input Area */}
                    <div className="flex-1 p-4 flex flex-col border-r border-cc-border min-w-[300px]">
                        <textarea
                            className="flex-1 w-full bg-cc-tile p-4 rounded-lg border border-cc-tile-border outline-none focus:ring-2 focus:ring-cc-accent/50 text-sm font-mono text-cc-text resize-none placeholder-cc-text-muted"
                            placeholder={`# Project Title\n## Category\n### Subcategory\n#### Sub-subcategory\n\nContent goes here...`}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            autoFocus
                        />
                        <div className="mt-2 text-xs text-cc-text-muted flex items-center justify-between">
                            <span>Supports Markdown Headers (#, ##, ###, ####)</span>
                            <span className={text.length > 0 ? "text-cc-accent" : ""}>{text.length} chars</span>
                        </div>
                    </div>

                    {/* Preview Area */}
                    <div className="w-80 bg-cc-elevated p-4 flex flex-col border-l border-cc-border">
                        <h3 className="text-xs font-bold uppercase text-cc-text-subtle tracking-wider mb-4">Preview Structure</h3>

                        {parsedPreview ? (
                            <div className="flex-1 overflow-y-auto space-y-4 custom-scrollbar">
                                <div className="space-y-1">
                                    <label className="text-[10px] text-cc-text-muted uppercase">Project Title</label>
                                    <p className="text-sm font-semibold text-cc-text truncate">{parsedPreview.title}</p>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] text-cc-text-muted uppercase">Sections ({parsedPreview.sections.length})</label>
                                    <div className="space-y-1.5">
                                        {parsedPreview.sections.length > 0 ? (
                                            parsedPreview.sections.map((s, i) => {
                                                // Indent calculation based on... well we don't have depth stored explicitly but we can infer or just list them flat with dots?
                                                // We only stored parentId. Visualization is tricky without tree build.
                                                // Let's just list them flat with indented title visually based on "order" or valid parent logic? 
                                                // Actually importParser assigns parents.
                                                // Let's just show a flat list but truncated.
                                                return (
                                                    <div key={i} className="flex items-center gap-2 text-xs text-cc-text-muted">
                                                        <div className={`w-1.5 h-1.5 rounded-full ${!s.parentId ? 'bg-cc-accent' : 'bg-cc-text-subtle'}`} />
                                                        <span className="truncate">{s.title}</span>
                                                    </div>
                                                )
                                            })
                                        ) : (
                                            <p className="text-xs text-cc-text-muted italic">No sections detected</p>
                                        )}
                                    </div>
                                </div>

                                <div className="space-y-1">
                                    <label className="text-[10px] text-cc-text-muted uppercase">Attachments ({parsedPreview.attachments.length})</label>
                                    {parsedPreview.attachments.length > 0 ? (
                                        <div className="flex flex-wrap gap-1">
                                            {parsedPreview.attachments.map((a, i) => (
                                                <span key={i} className="px-1.5 py-0.5 bg-cc-tile rounded text-[10px] text-cc-text-muted border border-cc-tile-border truncate max-w-[120px]">
                                                    {a.name}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-xs text-cc-text-muted italic">No attachments found</p>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="flex-1 flex flex-col items-center justify-center text-cc-text-muted opacity-50">
                                <FileText size={48} className="mb-2" />
                                <p className="text-xs text-center">Start typing to see<br />structure preview</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-cc-elevated border-t border-cc-border flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg text-sm font-medium text-cc-text-muted hover:bg-cc-tile-hover transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => {
                            if (parsedPreview) {
                                onImport({ ...parsedPreview, content: "" }); // Content is inside sections
                                onClose();
                            }
                        }}
                        disabled={!parsedPreview || parsedPreview.sections.length === 0}
                        className="px-4 py-2 bg-cc-accent text-cc-accent-text rounded-lg text-sm font-medium hover:bg-cc-accent-hover shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all"
                    >
                        <Check size={16} />
                        {existingTitle ? 'Merge Import' : 'Create Project'}
                    </button>
                </div>

            </div>
        </div>
    );
};
